import React, { useState } from 'react';
import { SlackPanel } from '../SlackIntegrationGraph';
import { CodeTerminal, TabIcon } from '../../pages/CompanyBrain';
import { ChatGPTPanel } from './ChatGPTPanel';
import { PillTabs } from './PillTabs';
import { SectionIntro } from './SectionIntro';

/**
 * Surfaces the co worker runs in. Slack and Teams are listed separately rather
 * than collapsed into one "chat" tab: a Teams shop should see Teams named on
 * the page, not discover it behind a toggle inside a Slack screenshot.
 */
type SurfaceKey = 'slack' | 'teams' | 'code' | 'chatgpt';

/**
 * The AI Co Worker section: one surface switcher sitting directly above the
 * frame it controls. Owns its own selection state so the same section can be
 * dropped into any page without threading props through it.
 */
export function CoWorkerPillar() {
  const [activeSurface, setActiveSurface] = useState<SurfaceKey>('slack');
  return <>
      <SectionIntro className="mx-auto mb-12 max-w-3xl text-center md:mb-16" data-id="element-451" data-mp="1y5autcwqctk9">
        <p className="mb-4 text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-452" data-mp="2re2opia8b88n">
          AI Co Worker
        </p>
        <h2 className="font-title text-4xl font-bold leading-[1.05] tracking-tight text-[#2B211A] md:text-[56px]" data-id="element-453" data-mp="2wcdyjt0ixiqv">
          An AI teammate
          <br data-id="element-454" data-mp="2ig8pnzl2y0hs" />
          inside your existing tools
        </h2>
        <p className="mx-auto mt-5 max-w-[54ch] text-lg leading-relaxed text-[#6B5D52]" data-id="element-455" data-mp="2nbfp2v7bgj0w">
          Add Giga to Slack, ChatGPT, Claude Code or Cowork, and talk to it like
          you would a normal employee.
        </p>
      </SectionIntro>

      <div className="mx-auto max-w-[820px]" data-id="element-456" data-mp="1znepibs48cxm">
        {/* Deliberately quieter and smaller than any page-level navigation —
         this is a local control, not a way around the page — but the platform
         logos stay so each surface is identifiable at a glance. */}
        <SurfaceTabsControl activeSurface={activeSurface} setActiveSurface={setActiveSurface} className="mb-8 hidden justify-center md:mb-10 md:flex" data-id="element-457" data-mp="1prbml3pfwa9m" />
        <SurfacePreview activeSurface={activeSurface} setActiveSurface={setActiveSurface} data-id="element-458" data-mp="8d29wm04mzz" />
      </div>
    </>;
}
function SurfacePreview({
  activeSurface,
  setActiveSurface
}: {
  activeSurface: SurfaceKey;
  setActiveSurface: (surface: SurfaceKey) => void;
}) {
  // On narrow screens the tabs become the frame's own tab strip, since a
  // centred pill row would wrap to two lines.
  const mobileTabs = <SurfaceTabsControl activeSurface={activeSurface} setActiveSurface={setActiveSurface} className="relative z-10 flex md:hidden" tabClassName="!flex-1 !justify-center !px-2 !py-2 !text-xs" quiet data-id="element-459" data-mp="j78wv91rv3ec" />;
  if (activeSurface === 'chatgpt') {
    return <div className="relative mx-auto w-full max-w-[760px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-460" data-mp="a8dphtmzrn9g">
        <div className="absolute -inset-3 hidden rounded-[28px] bg-[#EA580C]/10 md:block" aria-hidden="true" data-id="element-461" data-mp="2uw8h2loyb4uw" />
        {mobileTabs}
        <div className="relative [&>div]:rounded-tl-none md:[&>div]:rounded-tl-[20px]" data-id="element-462" data-mp="2pwdpsh19kd9b">
          <ChatGPTPanel heightClass="h-[520px] sm:h-[600px]" data-id="element-463" data-mp="2fu9r3ylpx2hi" />
        </div>
      </div>;
  }
  if (activeSurface === 'code') {
    return <div className="relative mx-auto w-full max-w-[760px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-464" data-mp="2kt33tot6kpgm">
        {mobileTabs}
        <CodeTerminal data-id="element-465" data-mp="2ui4m5ln77ixr" />
      </div>;
  }
  // Width matches SlackPanel's own max-width so the orange halo hugs the frame
  // instead of showing a strip of background down each side. Slack and Teams
  // are separate tabs here, so the panel's own platform toggle is suppressed —
  // the tab row above it already made that choice.
  return <div className="relative mx-auto w-full max-w-[720px] rounded-[28px] bg-[#EA580C]/10 p-2 pt-1 md:bg-transparent md:p-0" data-id="element-466" data-mp="38j769sumuj61">
      <div className="absolute -inset-3 hidden rounded-[28px] bg-[#EA580C]/10 md:block" aria-hidden="true" data-id="element-467" data-mp="3dgh4ny002560" />
      {mobileTabs}
      <div className="relative text-left [&>div]:rounded-tl-none md:[&>div]:rounded-tl-2xl" data-id="element-468" data-mp="1j37us53jkhn8">
        <SlackPanel className="w-full" heightClass="h-[520px] sm:h-[600px]" platform={activeSurface === 'teams' ? 'teams' : 'slack'} showPlatformToggle={false} data-id="element-469" data-mp="39kbejys7rs1r" />
      </div>
    </div>;
}
function SurfaceTabsControl({
  activeSurface,
  setActiveSurface,
  className = '',
  tabClassName = '',
  quiet = false
}: {
  activeSurface: SurfaceKey;
  setActiveSurface: (surface: SurfaceKey) => void;
  className?: string;
  tabClassName?: string;
  quiet?: boolean;
}) {
  if (quiet) {
    const tabs = [{
      surface: 'slack' as const,
      label: 'Slack',
      icon: <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg" fallback="S" data-id="element-470" data-mp="2lwbol12q85o8" />
    }, {
      surface: 'teams' as const,
      label: 'Teams',
      icon: <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/microsoft-teams.svg" fallback="T" data-id="element-471" data-mp="2uv6su7m7n5ow" />,
      ariaLabel: 'Microsoft Teams'
    }, {
      surface: 'code' as const,
      label: 'Claude',
      icon: <TabIcon slug="claude" fallback="C" data-id="element-472" data-mp="225zv3aukyk4p" />,
      ariaLabel: 'Claude'
    }, {
      surface: 'chatgpt' as const,
      label: 'ChatGPT',
      icon: <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg" fallback="AI" data-id="element-473" data-mp="mffjxhkjv5r9" />
    }];
    return <div className={`w-full items-center gap-0 ${className}`} role="tablist" aria-label="Chat surface" data-id="element-474" data-mp="8okgikstlgv2">
        {tabs.map(tab => {
        const active = activeSurface === tab.surface;
        return <button key={tab.surface} type="button" role="tab" aria-selected={active} aria-label={tab.ariaLabel} onClick={() => setActiveSurface(tab.surface)} className={`inline-flex items-center gap-1.5 rounded-t-[16px] border border-transparent px-2 py-2 text-xs text-[#2B211A] transition-colors ${active ? 'bg-white font-bold shadow-sm' : 'font-semibold text-[#6B5D52]'} ${tabClassName}`} data-id="element-475" data-mp="11c6khu5ymnig">
              {tab.icon}
              {tab.label}
            </button>;
      })}
      </div>;
  }
  // Same control as the architecture diagram's preset tabs, with each
  // platform's logo added so the surfaces are identifiable at a glance.
  return <div className={`w-full items-center ${className}`} data-id="element-476" data-mp="2x0g619bni54y">
      <PillTabs ariaLabel="Chat surface" value={activeSurface} onChange={id => setActiveSurface(id as SurfaceKey)} className={tabClassName} items={[{
      id: 'slack',
      label: 'Slack',
      logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg'
    }, {
      id: 'teams',
      label: 'Teams',
      ariaLabel: 'Microsoft Teams',
      logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/microsoft-teams.svg'
    }, {
      id: 'code',
      label: 'Claude',
      ariaLabel: 'Claude',
      logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/claude-ai.svg'
    }, {
      id: 'chatgpt',
      label: 'ChatGPT',
      logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/chatgpt.svg'
    }]} data-id="element-477" data-mp="2t0edpqrt2o2c" />
    </div>;
}